/**
 * AdminWorkspace — Platform Governance Workspace
 * 12-module institutional-grade admin control center
 * Replaces legacy AdminGovernance.js with complete governance coverage
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Shield, Layers, Package, CheckCircle2,
  FolderOpen, FileText, DollarSign, Map, Activity, Settings,
  RefreshCw, AlertTriangle, Building2, Lock,
  TrendingUp, Clock, CheckSquare, XCircle, Globe
} from "lucide-react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const NAVY  = "bg-[#0F172A]";
const TEAL  = "text-teal-400";
const SLATE = "text-slate-400";

// ── Navigation ────────────────────────────────────────────────────────────────
const NAV_MODULES = [
  { id: "overview",      label: "Admin Overview",               icon: LayoutDashboard, path: "/admin"              },
  { id: "orgs",          label: "Organizations & Users",        icon: Users,           path: "/admin/orgs"         },
  { id: "roles",         label: "Roles & Permissions",          icon: Shield,          path: "/admin/roles"        },
  { id: "templates",     label: "Workspace Templates",          icon: Layers,          path: "/admin/templates"    },
  { id: "config-packs",  label: "Config Packs & Methodologies", icon: Package,         path: "/admin/config-packs" },
  { id: "verification",  label: "Verification & Approvals",     icon: CheckCircle2,    path: "/admin/verification" },
  { id: "evidence",      label: "Evidence & Audit Oversight",   icon: FolderOpen,      path: "/admin/evidence"     },
  { id: "reports",       label: "Reports & Output Governance",  icon: FileText,        path: "/admin/reports"      },
  { id: "deals",         label: "Deal & Pipeline Oversight",    icon: DollarSign,      path: "/admin/deals"        },
  { id: "states",        label: "State & Programme Oversight",  icon: Map,             path: "/admin/states"       },
  { id: "system",        label: "System Health & Logs",         icon: Activity,        path: "/admin/system"       },
  { id: "settings",      label: "Platform Settings",            icon: Settings,        path: "/admin/settings"     },
];

// ── Utility components ────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = "text-teal-400", testid }) {
  return (
    <MetricCard label={label} value={value ?? "—"} sub={sub} color={color} testid={testid} />
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{title}</p>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    active:        "bg-emerald-100 text-emerald-700",
    draft:         "bg-slate-100 text-slate-600",
    submitted:     "bg-amber-100 text-amber-700",
    under_review:  "bg-blue-100 text-blue-700",
    verified:      "bg-emerald-100 text-emerald-700",
    rejected:      "bg-red-100 text-red-700",
    operational:   "bg-emerald-100 text-emerald-700",
    inactive:      "bg-slate-100 text-slate-400",
  }[status] || "bg-slate-100 text-slate-500";
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg}`}>
      {status?.replace(/_/g, " ").toUpperCase()}
    </span>
  );
}

function Loading() {
  return <div className="flex items-center justify-center py-12"><RefreshCw size={16} className="animate-spin text-teal-500" /></div>;
}

// ── MODULE 1: Admin Overview ──────────────────────────────────────────────────
function AdminOverview({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/workspace/overview")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const kpis = data?.kpis || {};
  const verif = data?.verification_summary || {};
  const orgBreakdown = data?.org_breakdown || {};
  const dealStages = data?.deal_stages || {};

  return (
    <div data-testid="admin-overview">
      <PageHeader
        label="ADMIN WORKSPACE"
        title="Platform Overview"
        subtitle="All workspaces · All segments"
        className="mb-6"
      />

      <div className="grid grid-cols-4 gap-4 mb-5">
        <StatCard label="Organizations" value={kpis.total_organizations} color="text-teal-600" testid="stat-orgs" />
        <StatCard label="Users" value={kpis.total_users} color="text-blue-600" testid="stat-users" />
        <StatCard label="Calculations Run" value={kpis.total_calculations} color="text-amber-600" testid="stat-calcs" />
        <StatCard label="Active Deals" value={kpis.total_deals} color="text-purple-600" testid="stat-deals" />
      </div>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="Programmes" value={kpis.total_programmes} color="text-slate-600" />
        <StatCard label="Evidence Records" value={kpis.total_evidence_records} color="text-slate-600" />
        <StatCard label="Verification Queue" value={kpis.verification_queue}
          color={kpis.verification_queue > 0 ? "text-amber-600" : "text-emerald-600"} />
        <StatCard label="Verified Periods" value={kpis.verified_periods} color="text-emerald-600" />
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        {/* Organization breakdown */}
        <SectionCard title="Orgs by Type">
          {Object.entries(orgBreakdown).map(([type, count]) => (
            <div key={type} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-xs text-slate-600 capitalize">{type?.replace(/_/g," ")}</p>
              <p className="text-xs font-bold text-slate-700">{count}</p>
            </div>
          ))}
        </SectionCard>
        {/* Verification status */}
        <SectionCard title="Verification Status">
          {Object.entries(verif).map(([status, count]) => (
            <div key={status} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
              <StatusBadge status={status} />
              <p className="text-xs font-bold text-slate-700">{count}</p>
            </div>
          ))}
        </SectionCard>
        {/* Deal stages */}
        <SectionCard title="Deal Stages">
          {Object.keys(dealStages).length === 0
            ? <p className="text-xs text-slate-400 italic">No active deals</p>
            : Object.entries(dealStages).map(([stage, count]) => (
              <div key={stage} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                <p className="text-xs text-slate-600">{stage}</p>
                <p className="text-xs font-bold text-slate-700">{count}</p>
              </div>
            ))}
        </SectionCard>
      </div>

      {/* Quick navigation */}
      <SectionCard title="Governance Modules" noPadding>
        <div className="grid grid-cols-3 divide-x divide-slate-50">
          {NAV_MODULES.slice(1).map(m => (
            <button key={m.id} onClick={() => navigate(m.path)}
              className="flex items-center gap-2 px-4 py-3 hover:bg-slate-50 text-left border-b border-slate-50 transition-colors">
              <m.icon size={12} className="text-slate-400 shrink-0" />
              <p className="text-xs text-slate-600 font-medium">{m.label}</p>
            </button>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

// ── OrgTemplateEditor — inline template/pack assignment ──────────────────────
const ORG_TYPES_EDIT = ["sme", "corporate", "project", "fi", "state"];
const SECTORS_EDIT   = ["energy","manufacturing","logistics","retail","services","construction",
                        "cement","banking","telecoms","agriculture","healthcare","mining",
                        "solar","efficiency","waste","general"];

function OrgTemplateEditor({ orgId, current, onSaved }) {
  const [open,      setOpen]      = useState(false);
  const [form,      setForm]      = useState({
    workspace_type: current.type || "sme",
    sector:         current.sector || "general",
    country:        current.country || "",
    state_id:       null,   // V103: FK dropdown — no free-text
  });
  const [saving,    setSaving]    = useState(false);
  const [template,  setTemplate]  = useState(null);
  const [stateList, setStateList] = useState([]);

  const loadTemplate = async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        client.get(`/admin/workspace/orgs/${orgId}/template`),
        client.get("/states/states-lookup"),
      ]);
      setTemplate(tRes.data);
      setStateList(sRes.data?.states || []);
      // Pre-fill state_id from existing org
      const existingStateId = tRes.data?.org_context?.state_id;
      if (existingStateId) setForm(prev => ({ ...prev, state_id: existingStateId }));
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send state_id (V103) — no state_name free text
      const body = {
        workspace_type: form.workspace_type,
        sector:         form.sector,
        country:        form.country,
        ...(form.state_id ? { state_id: form.state_id } : {}),
      };
      await client.patch(`/admin/workspace/orgs/${orgId}/template`, body);
      await onSaved();
      setOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed.");
    }
    setSaving(false);
  };

  return (
    <>
      <button onClick={() => { setOpen(v => !v); if (!open) loadTemplate(); }}
        className="text-xs font-bold text-teal-600 hover:underline"
        data-testid={`edit-template-${orgId}`}>
        {open ? "Close" : "Edit Template"}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl p-5 w-96 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid={`template-editor-${orgId}`}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Assign Template & Config Pack
            </p>
            <p className="text-xs font-bold text-slate-900 mb-4">{current.name}</p>

            {template && (
              <div className="bg-teal-50 border border-teal-100 rounded-xl px-3 py-2 mb-4">
                <p className="text-xs font-bold text-teal-600">Active: {template.active_template?.label} / {template.active_pack?.label}</p>
              </div>
            )}

            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Workspace Template</label>
                <select value={form.workspace_type} onChange={e => setForm({...form, workspace_type: e.target.value})}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="template-type-select">
                  {ORG_TYPES_EDIT.map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)} Workspace</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-0.5">Governs which workspace shell and nav the org uses.</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Sector / Config Pack</label>
                <select value={form.sector} onChange={e => setForm({...form, sector: e.target.value})}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="template-sector-select">
                  {SECTORS_EDIT.map(s => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-400 mt-0.5">Sets input schema, emission factors, intensity metrics, and benchmark.</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Country</label>
                <input value={form.country} onChange={e => setForm({...form, country: e.target.value})}
                  placeholder="e.g. Nigeria" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
                <p className="text-xs text-slate-400 mt-0.5">Used for state aggregation and Article 6 readiness.</p>
              </div>
              {/* V103: State dropdown from reference table — no free-text */}
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">
                  State / Province (normalized)
                </label>
                <select
                  value={form.state_id || ""}
                  onChange={e => setForm({...form, state_id: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="state-id-select">
                  <option value="">— Not assigned —</option>
                  {stateList.map(s => (
                    <option key={s.id} value={s.id}>{s.display}</option>
                  ))}
                </select>
                <p className="text-xs text-teal-600 mt-0.5">
                  State FK — used for cross-state validation (integer comparison, not string).
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
                data-testid="template-save-btn">
                {saving ? "Saving..." : "Save Assignment"}
              </button>
              <button onClick={() => setOpen(false)}
                className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Module 2: Organizations & Users ───────────────────────────────────────────
function AdminOrgsUsers({ navigate }) {
  const [tab, setTab] = useState("orgs");
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [updating, setUpdating] = useState(null);
  const [panel, setPanel] = useState(null);   // "create-org" | "create-user" | null

  // ── Form state for create-org ──────────────────────────────────────────────
  const [orgForm, setOrgForm] = useState({
    name: "", type: "sme", sector: "", country: "", description: "", employee_count: "",
    state_id: null,  // V103: state FK from reference table
  });
  const [stateOptions, setStateOptions] = useState([]);

  // Load state reference data for dropdowns
  useEffect(() => {
    client.get("/states/states-lookup").then(r => setStateOptions(r.data?.states || [])).catch(() => {});
  }, []);

  // ── Form state for create-user (full workflow) ─────────────────────────────
  const [userForm, setUserForm] = useState({
    email: "", first_name: "", last_name: "", role: "sme", org_name: "",
    sector: "", country: "", workspace_type: "sme", default_workspace_path: "/sme/overview",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formMsg, setFormMsg] = useState(null);

  const WORKSPACE_PATHS = {
    sme:       "/sme/overview",
    corporate: "/corporate",
    project:   "/project/workspace",
    fi:        "/fi",
    states:    "/states/overview",
  };
  const ORG_TYPES = ["sme", "corporate", "project", "fi", "state"];
  const SECTORS = ["energy", "manufacturing", "logistics", "retail", "services", "construction",
                   "cement", "banking", "telecoms", "agriculture", "healthcare", "mining", "general"];

  const load = async () => {
    setLoading(true);
    try {
      const [oRes, uRes] = await Promise.all([
        client.get("/admin/organizations?limit=100"),
        client.get("/admin/users?limit=100"),
      ]);
      setOrgs(oRes.data?.organizations || oRes.data || []);
      setUsers(uRes.data?.users || uRes.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUserAction = async (userId, action) => {
    setUpdating(userId);
    try {
      await client.patch(`/admin/workspace/users/${userId}/status`, {
        status: action === "activate" ? "active" : "suspended",
      });
      await load();
    } catch {}
    setUpdating(null);
  };

  const handleCreateOrg = async () => {
    if (!orgForm.name) { setFormMsg({ type: "error", text: "Organization name is required." }); return; }
    setSubmitting(true); setFormMsg(null);
    try {
      await client.post("/organizations", {
        name:         orgForm.name,
        type:         orgForm.type,
        sector:       orgForm.sector,
        country:      orgForm.country,
        description:  orgForm.description,
        employee_count: orgForm.employee_count ? parseInt(orgForm.employee_count) : null,
        state_id:     orgForm.state_id || null,
      });
      setFormMsg({ type: "success", text: `Organization "${orgForm.name}" created.` });
      setOrgForm({ name: "", type: "sme", sector: "", country: "", description: "", employee_count: "", state_id: null });
      await load();
    } catch (e) {
      setFormMsg({ type: "error", text: e.response?.data?.detail || "Creation failed." });
    }
    setSubmitting(false);
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.first_name || !userForm.org_name) {
      setFormMsg({ type: "error", text: "Email, first name, and organization name are required." }); return;
    }
    setSubmitting(true); setFormMsg(null);
    try {
      const res = await client.post("/onboarding/admin/create-user", {
        email:          userForm.email,
        first_name:     userForm.first_name,
        last_name:      userForm.last_name || "",
        role:           userForm.role,
        org_name:       userForm.org_name,
        sector:         userForm.sector,
        country:        userForm.country,
        workspace_type: userForm.workspace_type || userForm.role,
        default_workspace_path: WORKSPACE_PATHS[userForm.role] || "/dashboard",
      });
      setFormMsg({
        type: "success",
        text: `User "${userForm.email}" created. Set-password link: ${window.location.origin}/set-password?token=${res.data?.invite_token || "N/A"}`,
      });
      setUserForm({ email: "", first_name: "", last_name: "", role: "sme", org_name: "",
                   sector: "", country: "", workspace_type: "sme", default_workspace_path: "/sme/overview" });
      await load();
    } catch (e) {
      setFormMsg({ type: "error", text: e.response?.data?.detail || "User creation failed." });
    }
    setSubmitting(false);
  };

  const filteredOrgs = orgs.filter(o =>
    !search || o.name?.toLowerCase().includes(search.toLowerCase()) ||
    o.type?.includes(search.toLowerCase())
  );
  const filteredUsers = users.filter(u =>
    !search || u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.includes(search.toLowerCase())
  );

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-orgs-users">
      <div className="bg-[#0F172A] rounded-xl p-5 mb-5">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Organizations & Users</h2>
            <p className="text-xs text-slate-400 mt-0.5">{orgs.length} organizations · {users.length} users</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setPanel(panel === "create-org" ? null : "create-org"); setFormMsg(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl ${panel === "create-org" ? "bg-white text-slate-900" : "border border-white/20 text-white hover:bg-white/10"}`}
              data-testid="btn-new-org">
              + New Organization
            </button>
            <button onClick={() => { setPanel(panel === "create-user" ? null : "create-user"); setFormMsg(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl ${panel === "create-user" ? "bg-teal-400 text-slate-900" : "border border-teal-400/30 text-teal-400 hover:bg-teal-400/10"}`}
              data-testid="btn-new-user">
              + New User
            </button>
          </div>
        </div>
      </div>

      {/* ── Create Organization panel ─────────────────────────────────────── */}
      {panel === "create-org" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="create-org-panel">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Create New Organization</p>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">Organization Name *</label>
              <input value={orgForm.name} onChange={e => setOrgForm({...orgForm, name: e.target.value})}
                placeholder="e.g. Okoowa Energy Ltd" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                data-testid="org-name-input" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Workspace Type</label>
              <select value={orgForm.type} onChange={e => setOrgForm({...orgForm, type: e.target.value})}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" data-testid="org-type-select">
                {ORG_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Sector / Industry</label>
              <select value={orgForm.sector} onChange={e => setOrgForm({...orgForm, sector: e.target.value})}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl">
                <option value="">Select sector...</option>
                {SECTORS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Country / Geography</label>
              <input value={orgForm.country} onChange={e => setOrgForm({...orgForm, country: e.target.value})}
                placeholder="e.g. Nigeria" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Employee Count (optional)</label>
              <input type="number" value={orgForm.employee_count} onChange={e => setOrgForm({...orgForm, employee_count: e.target.value})}
                placeholder="e.g. 150" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
            </div>
            {/* V103: State from reference table */}
            <div className="col-span-2">
              <label className="block text-xs font-bold text-slate-600 mb-1">State / Province</label>
              <select value={orgForm.state_id || ""} onChange={e => setOrgForm({...orgForm, state_id: e.target.value ? parseInt(e.target.value) : null})}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                data-testid="org-state-select">
                <option value="">— Not assigned (national level) —</option>
                {stateOptions.map(s => (
                  <option key={s.id} value={s.id}>{s.display}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-0.5">State FK — used for sub-national state-level cross-state validation.</p>
            </div>
          </div>
          {formMsg && (
            <div className={`rounded-xl px-3 py-2 mb-3 text-xs font-medium ${formMsg.type === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {formMsg.text}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={handleCreateOrg} disabled={submitting}
              className="px-4 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
              data-testid="create-org-submit">
              {submitting ? "Creating..." : "Create Organization"}
            </button>
            <button onClick={() => { setPanel(null); setFormMsg(null); }}
              className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Create User panel (full workflow) ────────────────────────────── */}
      {panel === "create-user" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4" data-testid="create-user-panel">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Create New User — Full Workflow</p>
          <p className="text-xs text-slate-400 mb-4">Creates user + organization + workspace assignment in a single action.</p>

          {/* Step 1: User identity */}
          <div className="border border-slate-100 rounded-xl p-4 mb-3">
            <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3">Step 1 — User Identity</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Email Address *</label>
                <input type="email" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})}
                  placeholder="user@example.com" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="user-email-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">First Name *</label>
                <input value={userForm.first_name} onChange={e => setUserForm({...userForm, first_name: e.target.value})}
                  placeholder="First name" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Last Name</label>
                <input value={userForm.last_name} onChange={e => setUserForm({...userForm, last_name: e.target.value})}
                  placeholder="Last name" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
              </div>
            </div>
          </div>

          {/* Step 2: Workspace assignment */}
          <div className="border border-slate-100 rounded-xl p-4 mb-3">
            <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3">Step 2 — Workspace & Role</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Platform Role</label>
                <select value={userForm.role}
                  onChange={e => setUserForm({...userForm, role: e.target.value,
                    default_workspace_path: WORKSPACE_PATHS[e.target.value] || "/dashboard"})}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" data-testid="user-role-select">
                  <option value="sme">SME</option>
                  <option value="corporate">Corporate</option>
                  <option value="project">Project Developer</option>
                  <option value="fi">Financial Institution</option>
                  <option value="states">States Governance</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Default Landing Path</label>
                <input value={userForm.default_workspace_path}
                  onChange={e => setUserForm({...userForm, default_workspace_path: e.target.value})}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl font-mono text-xs" />
                <p className="text-xs text-slate-400 mt-0.5">Auto-set by role. Override if needed.</p>
              </div>
            </div>
          </div>

          {/* Step 3: Organization */}
          <div className="border border-slate-100 rounded-xl p-4 mb-3">
            <p className="text-xs font-bold text-teal-600 uppercase tracking-wider mb-3">Step 3 — Organization</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Organization Name *</label>
                <input value={userForm.org_name} onChange={e => setUserForm({...userForm, org_name: e.target.value})}
                  placeholder="e.g. Acme Energy Ltd" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="user-org-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Sector / Industry</label>
                <select value={userForm.sector} onChange={e => setUserForm({...userForm, sector: e.target.value})}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl">
                  <option value="">Select sector...</option>
                  {SECTORS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Country</label>
                <input value={userForm.country} onChange={e => setUserForm({...userForm, country: e.target.value})}
                  placeholder="e.g. Nigeria" className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl" />
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-teal-50 border border-teal-100 rounded-xl px-4 py-2.5 mb-3">
            <p className="text-xs text-teal-700 font-medium">
              User will be created as <strong>Active</strong> immediately.
              A set-password invite link will be generated — copy it and send to the user.
            </p>
          </div>

          {formMsg && (
            <div className={`rounded-xl px-3 py-2 mb-3 text-xs ${formMsg.type === "success" ? "bg-emerald-50 text-emerald-700 font-medium" : "bg-red-50 text-red-700 font-medium"}`}>
              {formMsg.text}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={handleCreateUser} disabled={submitting}
              className="px-5 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50"
              data-testid="create-user-submit">
              {submitting ? "Creating..." : "Create User + Organization + Workspace"}
            </button>
            <button onClick={() => { setPanel(null); setFormMsg(null); }}
              className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Tabs and list ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1 p-1 bg-white border border-slate-200 rounded-xl">
          {[["orgs","Organizations"],["users","Users"]].map(([id,l]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${tab === id ? "bg-[#0F172A] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
              {l} ({id === "orgs" ? filteredOrgs.length : filteredUsers.length})
            </button>
          ))}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search..." className="px-3 py-1.5 text-xs border border-slate-200 rounded-xl flex-1 max-w-xs" />
      </div>

      {tab === "orgs" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Name","Template","Sector/Pack","Country","State Status","ID","Actions"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-4 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filteredOrgs.map(o => (
                <React.Fragment key={o.id}>
                  <tr className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-2.5 font-semibold text-slate-900">{o.name}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={o.type} /></td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{o.sector || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500">{o.country || "—"}</td>
                    {/* V104: State completeness badge */}
                    <td className="px-4 py-2.5">
                      {o.state_assignment_status ? (
                        <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${o.state_assignment_status.badge}`}
                          title={o.state_assignment_status.note}
                          data-testid={`state-status-${o.id}`}>
                          {o.state_assignment_status.label}
                        </span>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{o.id}</td>
                    <td className="px-4 py-2.5">
                      <OrgTemplateEditor orgId={o.id} current={o} onSaved={load} />
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "users" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Email","Role","Status","Workspace","Actions"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-4 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{u.email}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={u.role} /></td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${u.account_status !== "suspended" ? "text-emerald-700 bg-emerald-50" : "text-red-600 bg-red-50"}`}>
                      {u.account_status !== "suspended" ? "Active" : "Suspended"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-400 text-xs">{u.default_workspace_path || u.active_role || "—"}</td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => handleUserAction(u.id, u.account_status !== "suspended" ? "suspend" : "activate")}
                      disabled={updating === u.id || u.role === "admin"}
                      className="text-xs font-bold text-teal-600 hover:underline disabled:opacity-30">
                      {updating === u.id ? "..." : u.account_status !== "suspended" ? "Suspend" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── MODULE 3: Roles & Permissions ─────────────────────────────────────────────
function AdminRoles() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/workspace/roles-matrix")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  const roles = data?.roles || {};

  return (
    <div data-testid="admin-roles">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Roles & Permissions</h2>
        <p className="text-xs text-slate-400 mt-0.5">Role capability matrix · Separation of duties enforced</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(roles).map(([role, cfg]) => (
          <div key={role} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-semibold text-slate-900">{cfg.label}</p>
                <p className="text-xs text-slate-400">{cfg.user_count} user(s) assigned</p>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                cfg.governance_level === "full" ? "bg-purple-100 text-purple-700" :
                "bg-teal-50 text-teal-700"
              }`}>{cfg.governance_level?.toUpperCase()}</span>
            </div>
            <div className="mb-2">
              <p className="text-xs font-bold text-slate-500 mb-1">Workspace access:</p>
              <div className="flex gap-1 flex-wrap">
                {cfg.workspace_access.map(w => (
                  <span key={w} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{w}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-slate-500 mb-1">Key capabilities:</p>
              {cfg.capabilities.slice(0,3).map((c, i) => (
                <p key={i} className="text-xs text-slate-500">• {c}</p>
              ))}
            </div>
            {cfg.restrictions?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-50">
                {cfg.restrictions.slice(0,2).map((r, i) => (
                  <p key={i} className="text-xs text-red-500 italic">! {r}</p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MODULE 4: Workspace Templates ─────────────────────────────────────────────
function AdminTemplates() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/workspace/templates")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  const templates = data?.templates || [];

  return (
    <div data-testid="admin-templates">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Workspace Templates</h2>
        <p className="text-xs text-slate-400 mt-0.5">Master workspace template registry · {data?.active} active · {data?.total} total</p>
      </div>
      <div className="space-y-3">
        {templates.map(t => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{t.label}</p>
                <p className="text-xs text-slate-400">{t.calculation_model} · v{t.version}</p>
              </div>
              <div className="flex gap-2 items-center">
                <StatusBadge status={t.status} />
                {t.usage_count !== null && (
                  <span className="text-xs text-slate-400">{t.usage_count} runs</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[
                ["Modules", t.modules],
                ["Methodology", t.methodology],
                ["Periodic", t.periodic ? "Yes" : "No"],
                ["Point-in-Time", t.point_in_time ? "Yes" : "No"],
              ].map(([l, v]) => (
                <div key={l} className="bg-slate-50 rounded-xl px-3 py-2 text-center">
                  <p className="text-xs font-bold text-slate-600">{v?.toString()?.slice(0,20)}</p>
                  <p className="text-xs text-slate-400">{l}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-1 flex-wrap">
              {t.nav_items?.slice(0,6).map((nav, i) => (
                <span key={i} className="text-xs bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded border border-teal-100">
                  {i+1}. {nav}
                </span>
              ))}
              {t.nav_items?.length > 6 && (
                <span className="text-xs text-slate-400">+{t.nav_items.length - 6} more</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MODULE 5: Config Packs & Methodologies ─────────────────────────────────────
function AdminConfigPacks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("sme");

  useEffect(() => {
    client.get("/admin/workspace/config-packs")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-config-packs">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Config Packs & Methodologies</h2>
        <p className="text-xs text-slate-400 mt-0.5">{data?.total_packs} total packs · {data?.methodology_versions?.length} methodology versions</p>
      </div>

      <div className="flex gap-1 p-1 bg-white border border-slate-200 rounded-xl mb-4">
        {[["sme","SME Industry"],["corporate","Corporate Sector"],["methodology","Methodologies"],["versions","Versions"]].map(([id,l]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${tab === id ? "bg-[#0F172A] text-white" : "text-slate-500 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "sme" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Industry Pack","Label","Priority Inputs","Scope 3 Weight","Status"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-4 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data?.sme_industry_packs || []).map(p => (
                <tr key={p.pack_id} className="border-b border-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-teal-700">{p.pack_id}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{p.label}</td>
                  <td className="px-4 py-2.5 text-slate-500">{p.priority_inputs} fields</td>
                  <td className="px-4 py-2.5 text-slate-500">{((p.scope3_weight || 0) * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "corporate" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Sector Pack","Label","IFRS S2 Sector","Scope 3 Weight","Status"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-4 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data?.corporate_sector_packs || []).map(p => (
                <tr key={p.pack_id} className="border-b border-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-teal-700">{p.pack_id}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-900">{p.label}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{p.ifrs_s2_sector || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500">{((p.scope3_weight || 0) * 100).toFixed(0)}%</td>
                  <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "methodology" && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Methodology Key","Applicable Methodologies","Workspace","Status"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-4 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data?.methodology_packs || []).map(p => (
                <tr key={p.pack_id} className="border-b border-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs text-teal-700">{p.pack_id}</td>
                  <td className="px-4 py-2.5 text-slate-500">{p.methodology_count} applicable</td>
                  <td className="px-4 py-2.5 text-slate-500">{p.workspace}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={p.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "versions" && (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Active Methodology Versions</p>
          <div className="space-y-1">
            {(data?.methodology_versions || []).map((v, i) => (
              <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
                <CheckCircle2 size={10} className="text-emerald-500" />
                <p className="text-xs font-mono text-slate-700">{v}</p>
                <StatusBadge status="active" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── MODULE 6: Verification & Approvals ────────────────────────────────────────
function AdminVerification({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const params = filter !== "all" ? `?status_filter=${filter}` : "";
    client.get(`/admin/workspace/verification-queue${params}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;
  const queue = data?.queue || [];
  const counts = data?.status_counts || {};

  const handleAdvance = async (item, toStatus) => {
    try {
      await client.post(`/verification/${item.workspace}/${item.period_label}/advance`, {
        to_status: toStatus,
        notes: `Admin action: ${toStatus}`,
      });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div data-testid="admin-verification">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Verification & Approvals</h2>
            <p className="text-xs text-slate-400 mt-0.5">Platform verification queue · {data?.pending_action} pending action</p>
          </div>
          {data?.pending_action > 0 && (
            <span className="text-xs font-bold px-3 py-1 bg-amber-400 text-slate-900 rounded-full">
              {data.pending_action} PENDING
            </span>
          )}
        </div>
      </div>

      {/* Status summary */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {["draft","submitted","under_review","verified","rejected"].map(s => (
          <div key={s} className={`rounded-xl p-3 text-center cursor-pointer border ${
            filter === s ? "border-teal-300 bg-teal-50" : "border-slate-100 bg-white hover:bg-slate-50"
          }`} onClick={() => setFilter(filter === s ? "all" : s)}>
            <p className="text-sm font-bold text-slate-700">{counts[s] || 0}</p>
            <p className="text-xs text-slate-400 uppercase">{s.replace(/_/g," ")}</p>
          </div>
        ))}
      </div>

      {queue.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
          <CheckCircle2 size={20} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-bold text-emerald-700">No items in verification queue</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Organization","Workspace","Period","Methodology","Status","Submitted By","Actions"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-3 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {queue.map(item => (
                <tr key={`${item.workspace}-${item.period_label}`} className="border-b border-slate-50 hover:bg-slate-50/40">
                  <td className="px-3 py-2.5 font-semibold text-slate-900">{item.org_name}</td>
                  <td className="px-3 py-2.5 text-slate-500 capitalize">{item.workspace}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-teal-700">{item.period_label}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-400">{item.methodology}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={item.verification_status} /></td>
                  <td className="px-3 py-2.5 text-xs text-slate-400">{item.submitted_by || "—"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      {item.verification_status === "submitted" && (
                        <button onClick={() => handleAdvance(item, "under_review")}
                          className="text-xs font-bold text-blue-600 hover:underline">Review</button>
                      )}
                      {item.verification_status === "under_review" && (
                        <>
                          <button onClick={() => handleAdvance(item, "verified")}
                            className="text-xs font-bold text-emerald-600 hover:underline">Verify</button>
                          <span className="text-slate-300">|</span>
                          <button onClick={() => handleAdvance(item, "rejected")}
                            className="text-xs font-bold text-red-500 hover:underline">Reject</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── MODULE 7: Evidence & Audit Oversight ─────────────────────────────────────
function AdminEvidence() {
  const [evid, setEvid]   = useState(null);
  const [logs, setLogs]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("evidence");

  useEffect(() => {
    Promise.all([
      client.get("/admin/workspace/evidence-health"),
      client.get("/admin/workspace/activity-logs?limit=30"),
    ]).then(([eRes, lRes]) => {
      setEvid(eRes.data);
      setLogs(lRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-evidence">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Evidence & Audit Oversight</h2>
        <p className="text-xs text-slate-400 mt-0.5">Evidence integrity · Audit trail · Activity logs</p>
      </div>

      <div className="flex gap-1 p-1 bg-white border border-slate-200 rounded-xl mb-4">
        {[["evidence","Evidence Health"],["audit","Audit Log"]].map(([id,l]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold ${tab === id ? "bg-[#0F172A] text-white" : "text-slate-500"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "evidence" && evid && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Total Evidence Records" value={evid.total_evidence_records} />
            <StatCard label="Orgs with Evidence" value={evid.orgs_with_evidence} />
            <StatCard label="Status" value={evid.gaps_detected ? "GAPS" : "OK"}
              color={evid.gaps_detected ? "text-amber-600" : "text-emerald-600"} />
          </div>
          {Object.entries(evid.by_status || {}).length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">By Status</p>
              {Object.entries(evid.by_status || {}).map(([s, c]) => (
                <div key={s} className="flex justify-between py-1 border-b border-slate-50 last:border-0">
                  <p className="text-xs text-slate-600">{s}</p>
                  <p className="text-xs font-bold">{c}</p>
                </div>
              ))}
            </div>
          )}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <p className="text-xs text-teal-700 font-bold">{evid.integrity_note}</p>
          </div>
        </div>
      )}

      {tab === "audit" && logs && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {(logs.logs || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">No audit log entries</div>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 border-b border-slate-100">
                {["Time","Action","Entity","By"].map(h => (
                  <th key={h} className="text-left text-xs text-slate-400 px-4 py-2 font-semibold">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(logs.logs || []).map((l, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/40">
                    <td className="px-4 py-2 text-xs text-slate-400 font-mono">{l.created_at?.slice(0,16)}</td>
                    <td className="px-4 py-2 font-semibold text-slate-900">{l.action}</td>
                    <td className="px-4 py-2 text-slate-500">{l.entity_type} #{l.entity_id}</td>
                    <td className="px-4 py-2 text-slate-400 text-xs">{l.admin_email || l.user_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── MODULE 8: Reports & Output Governance ─────────────────────────────────────
function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/workspace/reports-governance")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-reports">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Reports & Output Governance</h2>
        <p className="text-xs text-slate-400 mt-0.5">Report catalogue · Methodology versioning · Output quality</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <StatCard label="Reportable Periods" value={data?.total_reportable_periods} />
        <StatCard label="Locked Periods" value={data?.locked_periods} color="text-emerald-600" />
        <StatCard label="Draft Periods" value={data?.draft_periods} color="text-amber-600" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Workspace Breakdown</p>
          {Object.entries(data?.workspace_breakdown || {}).map(([ws, count]) => (
            <div key={ws} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-xs text-slate-700 capitalize">{ws}</p>
              <p className="text-xs font-bold">{count} periods</p>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Available Report Types</p>
          {(data?.available_report_types || []).map((rt, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-xs text-slate-700">{rt.type}</p>
              <div className="flex gap-1.5">
                <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{rt.pages}pp</span>
                <span className="text-xs bg-teal-50 text-teal-600 px-1.5 py-0.5 rounded">{rt.workspace}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 bg-white border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Methodology Versions Active</p>
        <div className="flex gap-1 flex-wrap">
          {(data?.methodology_versions_in_use || []).map((v, i) => (
            <span key={i} className="text-xs bg-teal-50 border border-teal-200 text-teal-700 px-2 py-0.5 rounded-full font-mono">{v}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── MODULE 9: Deal & Pipeline Oversight ───────────────────────────────────────
function AdminDealsModule({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/workspace/pipeline-overview")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div data-testid="admin-deals">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Deal & Pipeline Oversight</h2>
        <p className="text-xs text-slate-400 mt-0.5">FI pipeline · Cross-workspace flows · Deal stage monitoring</p>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-5">
        <StatCard label="Total FI Deals" value={data?.total_fi_deals} />
        <StatCard label="Programmes" value={data?.total_programmes} />
        <StatCard label="States → FI" value={data?.cross_workspace_flows?.states_to_fi} />
        <StatCard label="Projects → FI" value={data?.cross_workspace_flows?.project_to_fi} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Deal Stage Breakdown</p>
          {Object.keys(data?.deal_stage_breakdown || {}).length === 0 ? (
            <p className="text-xs text-slate-400 italic">No active deals in pipeline</p>
          ) : (
            Object.entries(data?.deal_stage_breakdown || {}).map(([s, c]) => (
              <div key={s} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
                <p className="text-xs text-slate-700 capitalize">{s?.replace(/_/g," ")}</p>
                <p className="text-xs font-bold">{c}</p>
              </div>
            ))
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Cross-Workspace Flows</p>
          {[
            ["SME → FI", "SME workspace data consumed by FI pipeline"],
            ["Corporate → Project → FI", "Via Carbon Project Structuring module"],
            ["Project → FI", `${data?.cross_workspace_flows?.project_to_fi || 0} deal(s) initiated`],
            ["States → Programme → FI", `${data?.cross_workspace_flows?.states_to_fi || 0} programme submission(s)`],
          ].map(([flow, desc]) => (
            <div key={flow} className="py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-xs font-bold text-slate-700">{flow}</p>
              <p className="text-xs text-slate-400">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex gap-2">
        <button onClick={() => navigate("/fi")}
          className="px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700">
          Open FI Workspace →
        </button>
        <button onClick={() => navigate("/states/programmes")}
          className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50">
          View State Programmes →
        </button>
      </div>
    </div>
  );
}

// ── MODULE 10: State & Programme Oversight ────────────────────────────────────
function AdminStatesModule({ navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/workspace/state-overview")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const programmes = data?.programmes || [];
  const byGeo = data?.programmes_by_geo || {};

  return (
    <div data-testid="admin-states">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">State & Programme Oversight</h2>
        <p className="text-xs text-slate-400 mt-0.5">{data?.state_orgs} state orgs · {data?.total_programmes} programmes</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-5">
        <StatCard label="State Orgs" value={data?.state_orgs} />
        <StatCard label="Total Programmes" value={data?.total_programmes} />
        <StatCard label="Submitted to FI" value={
          Object.values(byGeo).reduce((s, v) => s + (v.submitted || 0), 0)
        } color="text-teal-600" />
      </div>

      {Object.entries(byGeo).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Programmes by Geography</p>
          {Object.entries(byGeo).map(([geo, data]) => (
            <div key={geo} className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-xs font-semibold text-slate-700 flex-1">{geo}</p>
              <span className="text-xs text-slate-500">{data.total} programmes</span>
              {data.submitted > 0 && (
                <span className="text-xs font-bold text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">{data.submitted} → FI</span>
              )}
            </div>
          ))}
        </div>
      )}

      {programmes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-100">
              {["Ref ID","Name","Geography","Type","Reduction","Status","FI Deal"].map(h => (
                <th key={h} className="text-left text-xs text-slate-400 px-3 py-2 font-semibold">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {programmes.map(p => (
                <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/40">
                  <td className="px-3 py-2 font-mono text-xs text-teal-700">{p.ref_id}</td>
                  <td className="px-3 py-2 font-semibold text-slate-900">{p.name}</td>
                  <td className="px-3 py-2 text-slate-500">{p.geography}</td>
                  <td className="px-3 py-2 text-slate-500 uppercase text-xs">{p.programme_type}</td>
                  <td className="px-3 py-2 text-teal-700 font-bold">{p.total_reduction_tco2e?.toFixed(0) || "—"} t</td>
                  <td className="px-3 py-2"><StatusBadge status={p.status} /></td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{p.fi_request_id || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── MODULE 11: System Health & Logs ───────────────────────────────────────────
function AdminSystem() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/system-health")
      .then(r => setHealth(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  const services = health?.services || {};

  return (
    <div data-testid="admin-system">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">System Health & Activity Logs</h2>
            <p className="text-xs text-slate-400 mt-0.5">Service monitoring · Operational status · Platform health</p>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
            health?.status === "operational" ? "bg-emerald-400 text-slate-900" : "bg-red-400 text-white"
          }`}>{health?.status?.toUpperCase()}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Service Status</p>
          {Object.entries(services).map(([service, status]) => (
            <div key={service} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <p className="text-xs text-slate-700 capitalize">{service}</p>
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${status === "healthy" ? "bg-emerald-500" : "bg-red-500"}`} />
                <p className={`text-xs font-bold ${status === "healthy" ? "text-emerald-600" : "text-red-600"}`}>
                  {status?.toUpperCase()}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Platform Info</p>
          {[
            ["Database Type", health?.database_type || "SQLite"],
            ["Database Status", health?.database || "healthy"],
            ["Last Check", health?.timestamp?.slice(0,16)],
            ["Platform Version", "DeCarbonIQ V94"],
            ["Workspaces", "5 (SME, Corporate, Project, FI, States)"],
          ].map(([l, v]) => (
            <div key={l} className="flex justify-between py-1.5 border-b border-slate-50 last:border-0">
              <p className="text-xs text-slate-500">{l}</p>
              <p className="text-xs font-bold text-slate-700">{v || "—"}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Operational Notes</p>
        {[
          "Database: SQLite in development, PostgreSQL-compatible for production deployment.",
          "API: FastAPI (async) with SQLAlchemy ORM. All routes prefix /api/.",
          "Auth: JWT (HS256), 12-hour token expiry.",
          "PDF generation: fpdf2 2.8.7 — multi-cell with explicit l_margin reset.",
          "Audit logs: AuditLog model records all admin governance actions.",
        ].map((note, i) => (
          <p key={i} className="text-xs text-slate-500 mb-1">• {note}</p>
        ))}
      </div>
    </div>
  );
}

// ── MODULE 12: Platform Settings ──────────────────────────────────────────────
function AdminSettings() {
  return (
    <div data-testid="admin-settings">
      <div className={`${NAVY} rounded-xl p-5 mb-5`}>
        <h2 className="text-lg font-bold text-white">Platform Settings</h2>
        <p className="text-xs text-slate-400 mt-0.5">System configuration · Standards · Disclaimer language</p>
      </div>

      <div className="space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Global Validation Language</p>
          {[
            ["Verification Disclaimer", "Platform-verified (internal) — NOT third-party VVB validated"],
            ["Article 6 Disclaimer", "Indicative only. Requires host country LoA and sovereign authorization."],
            ["Financial Output Disclaimer", "INDICATIVE ONLY. Not investment advice. Based on published market ranges."],
            ["Carbon Credit Disclaimer", "DeCarbonIQ does not register projects or issue carbon credits."],
          ].map(([label, text]) => (
            <div key={label} className="mb-3 pb-3 border-b border-slate-50 last:border-0">
              <p className="text-xs font-bold text-slate-600 mb-0.5">{label}</p>
              <p className="text-xs text-slate-400 italic">"{text}"</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Active Platform Standards</p>
          {[
            ["GHG Protocol Corporate Standard", "2011 Revised Edition", "active"],
            ["GHG Protocol Scope 3 Standard", "2011", "active"],
            ["IPCC 2006 Guidelines", "Vol. 2 (Energy) + Vol. 3 (Industrial)", "active"],
            ["IEA World Energy Balances", "2023 (grid emission factors)", "active"],
            ["GLEC Framework", "2023 (logistics)", "active"],
            ["IFRS S2", "Climate-related Disclosures (intensity)", "active"],
            ["Portfolio Intelligence", "DCI-PORTFOLIO-INTELLIGENCE-V3.0", "active"],
          ].map(([std, version, status]) => (
            <div key={std} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
              <div>
                <p className="text-xs font-semibold text-slate-700">{std}</p>
                <p className="text-xs text-slate-400">{version}</p>
              </div>
              <StatusBadge status={status} />
            </div>
          ))}
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-xs text-amber-700 font-bold mb-1">Important: Configuration Safety</p>
          <p className="text-xs text-amber-600">
            All database credentials, API keys, and sensitive configuration are managed via .env files only.
            Never expose credential management in the UI. Use environment variables exclusively.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main AdminWorkspace ───────────────────────────────────────────────────────
export default function AdminWorkspace() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();

  // Determine active module from URL
  const pathParts = location.pathname.split("/admin/");
  const activeId  = pathParts[1] || "overview";

  // Map path → component
  const moduleMap = {
    "":              <AdminOverview navigate={navigate} />,
    "overview":      <AdminOverview navigate={navigate} />,
    "orgs":          <AdminOrgsUsers navigate={navigate} />,
    "roles":         <AdminRoles />,
    "templates":     <AdminTemplates />,
    "config-packs":  <AdminConfigPacks />,
    "verification":  <AdminVerification navigate={navigate} />,
    "evidence":      <AdminEvidence />,
    "reports":       <AdminReports />,
    "deals":         <AdminDealsModule navigate={navigate} />,
    "states":        <AdminStatesModule navigate={navigate} />,
    "system":        <AdminSystem />,
    "settings":      <AdminSettings />,
  };

  const activeModule = moduleMap[activeId] || moduleMap["overview"];

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8FAFC]">
      {/* Admin sidebar */}
      <div className="w-56 bg-[#0F172A] flex flex-col shrink-0 overflow-y-auto">
        <div className="px-4 py-5 border-b border-white/5">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">DeCarbonIQ</p>
          <p className="text-xs font-bold text-white">Platform Governance</p>
          <p className="text-xs text-slate-500 mt-0.5">Admin Workspace · V94</p>
        </div>
        <nav className="flex-1 px-2 py-3">
          {NAV_MODULES.map((m, i) => {
            const isActive = activeId === m.id || (m.id === "overview" && !activeId);
            return (
              <button key={m.id}
                onClick={() => navigate(m.path)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl mb-0.5 text-left transition-colors ${
                  isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`admin-nav-${m.id}`}>
                <m.icon size={13} className="shrink-0" />
                <span className="text-xs font-semibold leading-tight">{m.label}</span>
              </button>
            );
          })}
        </nav>
        {/* Platform version footer */}
        <div className="px-4 py-3 border-t border-white/5">
          <p className="text-xs text-slate-600 uppercase tracking-wider font-semibold">Platform Governance</p>
          <p className="text-xs text-slate-600">DeCarbonIQ V98</p>
        </div>
      </div>

      {/* Module content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 lg:p-8">
          {activeModule}
        </div>
      </div>
    </div>
  );
}
